import React, { useState, useRef } from 'react';
import { X, Camera, Video, MapPin, Send } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import api from '../../../services/api';
import VideoTrimmer from './VideoTrimmer';
import './StoryCreator.css';


const StoryCreator = ({ onClose, onStoryCreated }) => {
  const { isDark } = useTheme();

  const [mediaType, setMediaType] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [location, setLocation] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showTrimmer, setShowTrimmer] = useState(false);
  const [videoFileForTrim, setVideoFileForTrim] = useState(null);
  const fileInputRef = useRef(null);


  const checkVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration;
        resolve(duration);
      };
      
      video.onerror = () => {
        reject(new Error('Impossible de lire la vidéo'));
      };
      
      video.src = URL.createObjectURL(file);
    });
  };

  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      alert(`Fichier trop volumineux (${(file.size / 1024 / 1024).toFixed(1)}MB). Taille maximale: 100MB. Veuillez compresser votre vidéo.`);
      return;
    }

    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (!isImage && !isVideo) {
      alert('Veuillez sélectionner une image ou une vidéo');
      return;
    }

    // Check video duration if it's a video
    if (isVideo) {

      try {
        const duration = await checkVideoDuration(file);
        if (duration > 30) {
          // Show trimmer for long videos
          setVideoFileForTrim(file);
          setShowTrimmer(true);
          return;
        }
      } catch (error) {
        console.error('Error checking video duration:', error);
        alert('Erreur lors de la vérification de la durée de la vidéo');
        return;
      }
    }

    setMediaType(isImage ? 'photo' : 'video');
    
    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaPreview(reader.result);
      // Upload after preview is shown
      uploadFile(file, isImage ? 'photo' : 'video');
    };
    reader.onerror = () => {
      console.error('FileReader error');
      alert('Erreur lors de la lecture du fichier');
    };
    reader.readAsDataURL(file);
  };

  const [trimParams, setTrimParams] = useState(null);

  const handleTrimComplete = async (trimData) => {
    // Check file size after trim selection
    if (trimData.file.size > MAX_FILE_SIZE) {
      alert(`Fichier trop volumineux (${(trimData.file.size / 1024 / 1024).toFixed(1)}MB). Taille maximale: 100MB. Veuillez compresser votre vidéo avant de l'uploader.`);
      setShowTrimmer(false);
      setVideoFileForTrim(null);
      return;
    }

    setShowTrimmer(false);
    setVideoFileForTrim(null);
    setMediaType('video');
    setTrimParams({
      startTime: trimData.startTime,
      endTime: trimData.endTime
    });
    
    // Show local preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaPreview(reader.result);
      uploadFile(trimData.file, 'video', {
        start_time: trimData.startTime,
        end_time: trimData.endTime
      });
    };
    reader.readAsDataURL(trimData.file);
  };



  const handleTrimCancel = () => {
    setShowTrimmer(false);
    setVideoFileForTrim(null);
    setMediaType(null);
  };




  const uploadFile = async (file, type, trimData = null) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      
      // Add trim parameters if provided
      if (trimData) {
        formData.append('start_time', trimData.start_time);
        formData.append('end_time', trimData.end_time);
      }

      console.log('=== UPLOAD DEBUG ===');
      console.log('File:', file.name, 'Size:', file.size, 'Type:', file.type);
      console.log('Trim data:', trimData);
      console.log('API baseURL:', api.defaults.baseURL);

      const response = await api.post('/media/upload', formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        },
      });

      console.log('Upload response:', response.data);

      if (response.data.success && response.data.url) {
        // Update preview with server URL after successful upload
        setMediaPreview(response.data.url);
        console.log('Upload successful, URL:', response.data.url);
      } else {
        console.error('Upload failed:', response.data);
        alert('Erreur upload: ' + (response.data.message || 'Réponse invalide'));
      }
    } catch (error) {
      console.error('=== UPLOAD ERROR ===');
      console.error('Error:', error.message);
      console.error('Response:', error.response?.data);
      console.error('Status:', error.response?.status);
      alert('Erreur upload: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsUploading(false);
    }
  };




  const handleSubmit = async () => {
    if (!mediaPreview) {
      alert('Veuillez sélectionner un média');
      return;
    }

    setIsUploading(true);

    try {
      const storyData = {
        media_type: mediaType,
        media_file: mediaPreview,
        caption: caption,
        location: location,
      };
      
      // Add trim parameters if video was trimmed
      if (trimParams && mediaType === 'video') {
        storyData.start_time = trimParams.startTime;
        storyData.end_time = trimParams.endTime;
      }

      const response = await api.post('/stories', storyData);

      if (response.data.success) {
        onStoryCreated?.(response.data.data);
        onClose();
      }
    } catch (error) {
      console.error('Story creation error:', error);
      alert('Erreur lors de la création de la story');
    } finally {
      setIsUploading(false);
    }
  };


  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            // Reverse geocoding
            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=fr`
            );
            const data = await response.json();
            setLocation(data.city || data.locality || 'Localisation inconnue');
          } catch (error) {
            setLocation(`${latitude.toFixed(2)}, ${longitude.toFixed(2)}`);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
        }
      );
    }
  };

  return (
    <div className={`story-creator-overlay ${isDark ? 'dark' : ''}`}>
      {showTrimmer && videoFileForTrim && (
        <VideoTrimmer
          videoFile={videoFileForTrim}
          onTrimComplete={handleTrimComplete}
          onCancel={handleTrimCancel}
        />
      )}

      <div className="story-creator">
        <div className="story-creator-header">

          <h3>Nouvelle Story</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="story-creator-content">
          {!mediaPreview ? (
            <div className="media-selector">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,video/*"
                style={{ display: 'none' }}
              />
              
              <button
                className="media-option"
                onClick={() => {
                  setMediaType('photo');
                  fileInputRef.current?.click();
                }}
              >
                <Camera size={48} />
                <span>Photo</span>
              </button>
              
              <button
                className="media-option"
                onClick={() => {
                  setMediaType('video');
                  fileInputRef.current?.click();
                }}
              >
                <Video size={48} />
                <span>Vidéo</span>
              </button>
            </div>
          ) : (
            <div className="media-preview">
              {mediaType === 'photo' ? (
                <div>
                  <img 
                    src={mediaPreview} 
                    alt="Preview" 
                    style={{maxWidth: '100%', maxHeight: '400px', objectFit: 'contain'}}
                    onError={(e) => {
                      console.error('Image failed to load:', mediaPreview);
                      e.target.onerror = null;
                      e.target.src = '/default-image.svg';
                    }}
                  />
                  <p style={{fontSize: '10px', color: '#666', wordBreak: 'break-all', marginTop: '8px'}}>
                    {mediaPreview?.startsWith('data:') ? 'Preview locale' : mediaPreview}
                  </p>
                </div>
              ) : (
                <video 
                  src={mediaPreview} 
                  controls 
                  style={{maxWidth: '100%', maxHeight: '400px'}}
                  onError={(e) => {
                    console.error('Video failed to load:', mediaPreview);
                  }}
                />
              )}
              
              {isUploading && (
                <div className="upload-progress" style={{marginTop: '10px'}}>
                  <div
                    className="progress-bar"
                    style={{ 
                      width: `${uploadProgress}%`,
                      height: '4px',
                      backgroundColor: '#4CAF50',
                      transition: 'width 0.3s'
                    }}
                  />
                  <span style={{fontSize: '12px', color: '#666'}}>{uploadProgress}%</span>
                </div>
              )}
            </div>
          )}


          {mediaPreview && !isUploading && (
            <div className="story-form">
              <textarea
                placeholder="Ajouter une légende..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={500}
              />
              
              <div className="location-input">
                <MapPin size={20} />
                <input
                  type="text"
                  placeholder="Ajouter un lieu"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
                <button onClick={getLocation} className="location-btn">
                  Localiser
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="story-creator-footer">
          {mediaPreview && (
            <button
              className="btn-secondary"
              onClick={() => {
                setMediaPreview(null);
                setMediaType(null);
                setCaption('');
                setLocation('');
                setTrimParams(null);
              }}
            >

              Changer
            </button>
          )}
          
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={!mediaPreview || isUploading}
          >
            {isUploading ? 'Publication...' : (
              <>
                <Send size={18} />
                Publier (24h)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoryCreator;
